// رسیدگی اسناد هزینه
import React, { useState, useEffect, useCallback } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePager, CountBadge, MoreButton } from '../../components/paging';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { ExcelButton, exportGrid } from '../../components/excel';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const fmtNum = n => (n ? Number(n).toLocaleString('fa-IR') : '');

const API = async (path, opt = {}) => {
  const res = await fetch(`/api/evaluation${path}`, {
    ...opt,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('access')}`,
      ...(opt.headers || {}),
    },
  });
  try { return await res.json(); } catch { return { ok: false, msg: 'خطای ارتباط با سرور' }; }
};

/* رنگ ردیف (آخرین شرط برنده است):
   برگشت سند = نارنجی · شروع رسیدگی = ارغوانی · خاتمه = فیروزه‌ای · ارسال به بودجه = نقره‌ای */
const ROW_BG = {
  return: '#ffcc80',
  begin:  '#f8bbd0',
  finish: '#b2ebf2',
  budget: '#e0e0e0',
};

const REPORT_DATE_FIELDS = [
  { key: 'document',  label: 'تاریخ سند هزینه' },
  { key: 'begin',     label: 'شروع رسیدگی' },
  { key: 'return',    label: 'برگشت سند' },
  { key: 'finish',    label: 'خاتمه رسیدگی' },
  { key: 'to_budget', label: 'ارسال به بودجه' },
];


/* ستون‌های خروجی اکسل هر گرید */
const XL_DOC = [
  { key: 'program_kind_name', label: 'نوع سند هزینه' },
  { key: 'document_no', label: 'سندهزینه', num: true },
  { key: 'document_date', label: 'تاریخ' },
  { key: 'remark', label: 'شرح' },
  { key: 'department', label: 'واحد' },
  { key: 'sum_pay', label: 'مبلغ', num: true },
  { key: 'discount', label: 'تخفیف', num: true },
  { key: 'added_tax', label: 'مالیات بر ارزش افزوده', num: true },
  { key: 'total_amount', label: 'جمع هزینه', num: true },
  { key: 'evaluation_title', label: 'نوع سند رسیدگی' },
  { key: 'letter_no', label: 'شماره دبیرخانه' },
  { key: 'letter_date', label: 'تاریخ دبیرخانه' },
  { key: 'detail_count', label: 'اقلام', num: true },
  { key: 'pay_kind_name', label: 'نوع پرداخت' },
  { key: 'account', label: 'حساب تدارکات' },
  { key: 'return_date', label: 'تاریخ برگشت سند' },
  { key: 'begin_date', label: 'تاریخ شروع رسیدگی' },
  { key: 'finish_date', label: 'تاریخ خاتمه رسیدگی' },
  { key: 'budget_date', label: 'تاریخ ارسال به بودجه' },
  { key: 'havaleh', label: 'حواله' },
  { key: 'sanad', label: 'سند' },
  { key: 'reject_count', label: 'واخواهی', num: true },
  { key: 'ok_count', label: 'تایید رسیدگی', num: true },
];

const xlDetail = (levels) => [
  { key: 'remark', label: 'شرح' },
  { key: 'receiver', label: 'فروشنده' },
  { key: 'account_no', label: 'حساب' },
  { key: 'sheba_error', label: 'خطای حساب شبا' },
  { key: 'bank', label: 'بانک' },
  { key: 'factor_no', label: 'فاکتور' },
  { key: 'factor_date', label: 'تاریخ' },
  { key: 'quantity', label: 'تعداد', num: true },
  { key: 'unit_price', label: 'مبلغ واحد', num: true },
  { key: 'sum_pay', label: 'مبلغ', num: true },
  { key: 'added_tax', label: 'مالیات بر ارزش افزوده', num: true },
  { key: 'discount', label: 'تخفیف', num: true },
  { key: 'payable', label: 'قابل پرداخت', num: true },
  { key: 'credit', label: 'منبع اعتبار' },
  { key: 'program', label: 'برنامه' },
  { key: 'project', label: 'پروژه' },
  ...[0, 1, 2, 3, 4, 5].map(i => ({
    key: `hazineh${i}`,
    label: (levels && levels[i]) || `سطح ${i + 1}`,
  })),
  { key: 'cheque_no', label: 'شماره چک' },
  { key: 'cheque_date', label: 'تاریخ چک' },
  { key: 'cheque_amount', label: 'مبلغ چک', num: true },
  { key: 'tax_ref_no', label: 'شماره رهگیری مالیاتی' },
  { key: 'tax_ref_date', label: 'تاریخ رهگیری مالیاتی' },
  { key: 'slip_id', label: 'شناسه پرداخت' },
  { key: 'bill_id', label: 'شناسه قبض' },
];

const XL_ITEMS = [
  { key: 'status', label: 'وضعیت' },
  { key: 'title', label: 'عنوان' },
  { key: 'remark', label: 'توضیحات' },
  { key: 'reject_reason', label: 'توضیح رسیدگی' },
  { key: 'event_date', label: 'تاریخ' },
  { key: 'event_time', label: 'زمان' },
  { key: 'history_count', label: 'سابقه', num: true },
];

export default function EvaluationDocumentList({ user, onBack }) {
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [toast, setToast]       = useState('');
  const pager  = usePager();     // فهرست اسناد هزینه — ۲۰۰ ردیف در هر صفحه
  const dPager = usePager();     // اقلام سند هزینه — ۲۰۰ ردیف در هر صفحه

  // فیلترهای وضعیت
  const [ready, setReady]       = useState(true);
  const [inEval, setInEval]     = useState(true);
  const [finished, setFinished] = useState(false);
  const [sent, setSent]         = useState(false);
  const [returned, setReturned] = useState(false);
  const [hazineh, setHazineh]   = useState(true);
  const [tamalok, setTamalok]   = useState(true);
  const [year, setYear]         = useState(null);
  const [years, setYears]       = useState([]);
  const [search, setSearch]     = useState('');
  const [sDetail, setSDetail]   = useState('');

  // انتخاب‌ها
  const [selected, setSelected]   = useState(null);   // سند هزینه
  const [details, setDetails]     = useState([]);     // اقلام سند
  const [selDetail, setSelDetail] = useState(null);   // قلم انتخابی
  const [docItems, setDocItems]   = useState([]);     // اقلام رسیدگیِ سند
  const [detItems, setDetItems]   = useState([]);     // اقلام رسیدگیِ قلم
  const [levels, setLevels]       = useState([]);     // عنوان سطوح کد هزینه

  // مودال‌ها
  const [modal, setModal]     = useState(null);
  const [reason, setReason]   = useState('');
  const [history, setHistory] = useState([]);
  const [report, setReport]   = useState(null);
  const [rFrom, setRFrom]     = useState('');
  const [rTo, setRTo]         = useState('');
  const [rField, setRField]   = useState('document');
  const [busy, setBusy]       = useState(false);

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };

  // ── سال‌های مالی ──
  useEffect(() => {
    (async () => {
      const j = await API('/hazineh-documents/years/');
      if (j.ok) {
        setYears(j.data || []);
        setYear(prev => prev ?? (j.data && j.data.length ? j.data[0] : j.current));
      }
    })();
  }, []);

  // ── فهرست اسناد هزینه ──
  const load = useCallback(async (opt = {}) => {
    const y = opt.year ?? year;
    if (!y) return;
    const page = opt.page ?? 1;
    setLoading(true); setError('');
    const p = new URLSearchParams({
      ready:    (opt.ready    ?? ready)    ? 1 : 0,
      in_eval:  (opt.in_eval  ?? inEval)   ? 1 : 0,
      finished: (opt.finished ?? finished) ? 1 : 0,
      sent:     (opt.sent     ?? sent)     ? 1 : 0,
      returned: (opt.returned ?? returned) ? 1 : 0,
      hazineh:  (opt.hazineh  ?? hazineh)  ? 1 : 0,
      tamalok:  (opt.tamalok  ?? tamalok)  ? 1 : 0,
      year:     y,
      search:        opt.search  ?? search,
      search_detail: opt.sDetail ?? sDetail,
      page,
    });
    const j = await API(`/hazineh-documents/?${p}`);
    setLoading(false);
    if (!j.ok) { setError(j.msg); return; }
    setData(prev => pager.merge(prev, j, page));
    if (page === 1) {
      // انتخاب قبلی را با همان شناسه نگه می‌داریم تا نوار عملیات نپرد
      setSelected(prev => prev ? ((j.data || []).find(x => x.id === prev.id) || null) : null);
    }
  }, [ready, inEval, finished, sent, returned, hazineh, tamalok, year, search, sDetail, pager]);

  useEffect(() => { if (year) load({ page: 1 }); }, [year]);   // eslint-disable-line

  // ── انتخاب سند → اقلام سند + اقلام رسیدگی سند ──
  const pick = async (r) => {
    if (selected?.id === r.id) {
      setSelected(null); setDetails([]); setSelDetail(null);
      setDocItems([]); setDetItems([]); return;
    }
    setSelected(r); setSelDetail(null); setDetItems([]);
    const [d, it] = await Promise.all([
      API(`/hazineh-documents/${r.id}/details/?page=1&year=${year}`),
      // اقلام رسیدگی فقط وقتی رسیدگی شروع شده باشد
      r.begin_date ? API(`/hazineh-documents/${r.id}/items/`) : Promise.resolve({ ok: true, data: [] }),
    ]);
    if (d.ok) { setDetails(prev => dPager.merge(prev, d, 1)); setLevels(d.levels || []); }
    if (it.ok) setDocItems(it.data);
  };

  const moreDetails = async (p) => {
    if (!selected) return;
    const d = await API(`/hazineh-documents/${selected.id}/details/?page=${p}&year=${year}`);
    if (d.ok) setDetails(prev => dPager.merge(prev, d, p));
  };

  // ── انتخاب قلم سند → اقلام رسیدگی آن قلم ──
  const pickDetail = async (r) => {
    if (selDetail?.id === r.id) { setSelDetail(null); setDetItems([]); return; }
    setSelDetail(r);
    if (!selected?.begin_date) { setDetItems([]); return; }
    const j = await API(`/hazineh-details/${r.id}/items/`);
    if (j.ok) setDetItems(j.data);
  };

  // ── گردش کار سند ──
  const runWorkflow = async (action, confirmMsg) => {
    if (!selected) return;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    const j = await API(`/hazineh-documents/${selected.id}/workflow/`,
      { method: 'POST', body: JSON.stringify({ action }) });
    setBusy(false);
    if (j.ok) { showToast(j.msg); load({ page: 1 }); }
    else setError(j.msg);
  };

  // ── تایید / واخواهی ──
  const verdict = async (action, payload = {}) => {
    setBusy(true);
    const j = await API('/hazineh-documents/verdict/', {
      method: 'POST',
      body: JSON.stringify({
        action,
        reason,
        hazineh_id: selected?.id || 0,
        hazineh_detail_id: payload.fromDetail ? (selDetail?.id || 0) : 0,
        document_id: payload.documentId || 0,
      }),
    });
    setBusy(false);
    if (j.ok) {
      setModal(null); setReason(''); showToast(j.msg);
      await refreshItems();          // گریدهای رسیدگی بدون از دست رفتن انتخاب
      await load({ page: 1 });
    } else setError(j.msg);
  };

  /* بازخوانی گریدهای ۳ و ۴ برای همان سند و همان قلم انتخاب‌شده */
  const refreshItems = async () => {
    if (!selected || !selected.begin_date) return;
    const [it, dt] = await Promise.all([
      API(`/hazineh-documents/${selected.id}/items/`),
      selDetail ? API(`/hazineh-details/${selDetail.id}/items/`)
                : Promise.resolve({ ok: true, data: [] }),
    ]);
    if (it.ok) setDocItems(it.data);
    if (dt.ok) setDetItems(dt.data);
  };

  const openHistory = async (docId) => {
    const j = await API(`/hazineh-documents/items/${docId}/history/`);
    if (j.ok) { setHistory(j.data); setModal('history'); }
  };

  // ── گزارش‌ها ──
  const openReport = async (kind) => {
    setBusy(true); setError('');
    let j;
    if (kind === 'list')      j = await API(`/hazineh-documents/report/?from=${rFrom}&to=${rTo}&date_field=${rField}&hazineh=${hazineh ? 1 : 0}&tamalok=${tamalok ? 1 : 0}`);
    else if (kind === 'returned') j = await API(`/hazineh-documents/returned/?from=${rFrom}&to=${rTo}`);
    else if (kind === 'deleted')  j = await API('/hazineh-documents/deleted-returned/');
    else j = await API(`/hazineh-documents/${selected.id}/reject-history/?kind=${kind === 'result' ? 'result' : 'history'}`);
    setBusy(false);
    if (!j.ok) { setError(j.msg); return; }
    if (j.msg) { showToast(j.msg); return; }
    setReport({ kind, ...j });
    setModal('report');
  };

  // ── نوار ابزار ──
  const chk = (label, val, set, key) => (
    <label style={lbl}>
      <input type="checkbox" checked={val}
        onChange={e => { set(e.target.checked); load({ [key]: e.target.checked, page: 1 }); }} />
      {label}
    </label>
  );

  const toolbar = (
    <>
      <select value={year || ''} onChange={e => setYear(Number(e.target.value))} style={ui.input}>
        {years.map(y => <option key={y} value={y}>{toFa(y)}</option>)}
      </select>
      {chk('آماده رسیدگی', ready, setReady, 'ready')}
      {chk('در حال رسیدگی', inEval, setInEval, 'in_eval')}
      {chk('رسیدگی‌شده', finished, setFinished, 'finished')}
      {chk('ارسال به بودجه', sent, setSent, 'sent')}
      {chk('فقط برگشتی', returned, setReturned, 'returned')}
      <span style={{ color: '#ccc' }}>|</span>
      {chk('هزینه‌ای', hazineh, setHazineh, 'hazineh')}
      {chk('تملک', tamalok, setTamalok, 'tamalok')}
      <input value={search} onChange={e => setSearch(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && load({ page: 1 })}
        style={{ ...ui.searchInput, minWidth: '150px' }} placeholder="جستجوی سند (شرح، واحد، مبلغ)..." />
      <input value={sDetail} onChange={e => setSDetail(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && load({ page: 1 })}
        style={{ ...ui.searchInput, minWidth: '150px' }} placeholder="جستجو در اقلام سند..." />
      <button onClick={() => load({ page: 1 })} style={ui.btnEdit}>🔍</button>
      {loading && <span>⏳</span>}
      <CountBadge shown={data.length} total={pager.total} loading={loading} unit="سند" />
      <ExcelButton title="اسناد هزینه درحال رسیدگی" columns={XL_DOC} rows={data}
        subtitle={`سال مالی ${toFa(year || '')}`} />
    </>
  );

  // ── نوار عملیات سند انتخاب‌شده — شرط نمایش هر عملیات بر اساس وضعیت سند ──
  const s = selected;
  const canStart       = s && !s.begin_date;
  const canFinish      = s && s.begin_date && !s.finish_date && s.reject_count === 0 && s.ok_count > 0;
  const canCancelEval  = s && s.begin_date && !s.finish_date;
  const canCancelFin   = s && s.finish_date && !s.budget_date;
  const canToBudget    = s && s.finish_date && !s.budget_date;
  const canCancelBud   = s && !!s.budget_date;
  const hasVerdicts    = s && (s.ok_count + s.reject_count) !== 0;
  const canJudge       = s && s.begin_date && !s.finish_date;

  const actions = s && (
    <div style={actBar}>
      <span style={actTitle}>سند {toFa(s.document_no)} — {s.document_date}</span>
      {canStart      && <button style={aBtn('#2e7d32')} disabled={busy} onClick={() => runWorkflow('start')}>▶ شروع رسیدگی</button>}
      {canCancelEval && <button style={aBtn('#c62828')} disabled={busy} onClick={() => runWorkflow('cancel_delete', 'آيا از لغو رسیدگی و حذف اقلام رسیدگی مطمئن هستيد؟')}>✕ لغو رسیدگی و حذف اقلام</button>}
      {canCancelEval && <button style={aBtn('#ef6c00')} disabled={busy} onClick={() => runWorkflow('cancel_return', 'آيا از لغو رسیدگی و برگشت سند مطمئن هستيد؟')}>↩ لغو رسیدگی و برگشت سند</button>}
      {canFinish     && <button style={aBtn('#00838f')} disabled={busy} onClick={() => runWorkflow('finish', 'آيا از خاتمه رسیدگی مطمئن هستيد؟')}>✔ پایان رسیدگی</button>}
      {canCancelFin  && <button style={aBtn('#5d4037')} disabled={busy} onClick={() => runWorkflow('cancel_finish', 'آيا از لغو پایان رسیدگی مطمئن هستيد؟')}>↺ لغو پایان رسیدگی</button>}
      {canToBudget   && <button style={aBtn('#1565c0')} disabled={busy} onClick={() => runWorkflow('to_budget')}>📤 ارسال به بودجه</button>}
      {canCancelBud  && <button style={aBtn('#616161')} disabled={busy} onClick={() => runWorkflow('cancel_to_budget', 'آيا از لغو ارسال به بودجه مطمئن هستيد؟')}>↺ لغو ارسال به بودجه</button>}
      {canJudge      && <button style={aBtn('#4a148c')} disabled={busy} onClick={() => { setReason(''); setModal('approve_all_all'); }}>✔✔ تایید همه موارد همه اقلام</button>}
      {hasVerdicts   && <button style={aBtn('#6a1b9a')} disabled={busy} onClick={() => openReport('history')}>📜 سوابق تایید و واخواهی</button>}
      {hasVerdicts   && <button style={aBtn('#00695c')} disabled={busy} onClick={() => openReport('result')}>📄 گزارش رسیدگی سند</button>}
      <span style={{ flex: 1 }} />
      <button style={aBtn('#37474f')} onClick={() => { setRFrom(''); setRTo(''); setModal('reports'); }}>📊 گزارش‌ها</button>
    </div>
  );

  // ── گرید اقلام رسیدگی (مشترک بین سند و قلم) ──
  const itemsGrid = (title, rows, fromDetail) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ ...gTitle, display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{title} {rows.length > 0 && `(${toFa(rows.length)})`}</span>
        <ExcelButton
          title={fromDetail ? 'رسیدگی ریز اقلام سند هزینه' : 'رسیدگی سند هزینه'}
          columns={XL_ITEMS} rows={rows} />
      </div>
      <div style={{ ...ui.tableWrap, maxHeight: '240px' }}>
        <table style={ui.table}>
          <thead><tr>
            <th style={ui.th}>وضعیت</th><th style={ui.th}>عنوان</th><th style={ui.th}>توضیحات</th>
            <th style={ui.th}>توضیح رسیدگی</th><th style={ui.th}>تاریخ</th><th style={ui.th}>زمان</th>
            <th style={ui.th}>سابقه</th><th style={ui.th}>عملیات</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} style={{ ...ui.td, textAlign: 'center', color: '#999' }}>
                {selected?.begin_date ? 'قلمی نیست' : 'رسیدگی هنوز شروع نشده است'}</td></tr>
            ) : rows.map(r => (
              <tr key={r.id}>
                <td style={{ ...ui.td, color: r.is_ok ? '#2e7d32' : (r.status ? '#c62828' : '#999'), fontWeight: 700 }}>{r.status || '—'}</td>
                <td style={ui.td}>{r.title}</td>
                <td style={ui.td}>{r.remark}</td>
                <td style={ui.td}>{r.reject_reason}</td>
                <td style={ui.td}>{r.event_date}</td>
                <td style={ui.td}>{r.event_time}</td>
                <td style={ui.tdNum}>{toFa(r.history_count)}</td>
                <td style={{ ...ui.td, whiteSpace: 'nowrap' }}>
                  {canJudge && <button style={miniBtn('#2e7d32')} onClick={() => { setReason(''); setModal({ t: 'approve', id: r.id }); }}>تایید</button>}
                  {canJudge && <button style={miniBtn('#c62828')} onClick={() => { setReason(''); setModal({ t: 'reject', id: r.id }); }}>واخواهی</button>}
                  {canJudge && <button style={miniBtn('#1565c0')} onClick={() => { setReason(''); setModal({ t: 'approve_all', fromDetail }); }}>تایید همه</button>}
                  <button style={miniBtn('#546e7a')} onClick={() => openHistory(r.id)}>سوابق</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const m = modal;
  const mt = typeof m === 'object' && m ? m.t : m;

  return (
    <ListPage user={user} onBack={onBack} title="رسیدگی اسناد هزینه"
      subtitle="بخش رسیدگی — سامانه ارجان" toolbar={toolbar} error={error}
        pager={pager} loading={loading} onMore={p => load({ page: p })}>

      {/* چیدمان دوتکه: گرید اسناد هزینه بالا با اسکرول خودش،
          اقلام سند و اقلام رسیدگی پایین و همیشه در دید. */}
      <div style={ui.splitWrap}>
      {actions}

      {/* گرید ۱ — اسناد هزینه */}
      <div style={ui.topPane}>
        <table style={{ ...ui.table, minWidth: '1800px' }}>
          <thead><tr>
            <th style={ui.th}>نوع سند</th><th style={ui.th}>سندهزینه</th><th style={ui.th}>تاریخ</th>
            <th style={ui.th}>شرح</th><th style={ui.th}>واحد</th>
            <th style={ui.th}>مبلغ</th><th style={ui.th}>تخفیف</th>
            <th style={ui.th}>مالیات بر ارزش افزوده</th><th style={ui.th}>جمع هزینه</th>
            <th style={ui.th}>نوع سند رسیدگی</th>
            <th style={ui.th}>شماره دبیرخانه</th><th style={ui.th}>تاریخ دبیرخانه</th>
            <th style={ui.th}>اقلام</th><th style={ui.th}>نوع پرداخت</th>
            <th style={ui.th}>حساب تدارکات</th>
            <th style={ui.th}>برگشت سند</th><th style={ui.th}>شروع رسیدگی</th>
            <th style={ui.th}>خاتمه رسیدگی</th><th style={ui.th}>ارسال به بودجه</th>
            <th style={ui.th}>حواله</th><th style={ui.th}>سند</th>
            <th style={ui.th}>واخواهی</th><th style={ui.th}>تایید رسیدگی</th>
          </tr></thead>
          <tbody>
            {data.length === 0 && !loading ? (
              <tr><td colSpan={23} style={ui.empty}>📭 سندی با این فیلترها یافت نشد</td></tr>
            ) : data.map(r => {
              const sel = selected?.id === r.id;
              const bg = sel ? '#fff8e1' : (ROW_BG[r.row_color] || undefined);
              return (
                <tr key={r.id} onClick={() => pick(r)}
                  style={{ cursor: 'pointer', background: bg,
                    outline: sel ? '2px solid #f9a825' : 'none' }}>
                  <td style={ui.td}>{r.program_kind_name}</td>
                  <td style={ui.tdNum}>{toFa(r.document_no)}</td>
                  <td style={ui.td}>{r.document_date}</td>
                  <td style={ui.td}>{r.remark}</td>
                  <td style={ui.td}>{r.department}</td>
                  <td style={ui.tdNum}>{fmtNum(r.sum_pay)}</td>
                  <td style={ui.tdNum}>{fmtNum(r.discount)}</td>
                  <td style={ui.tdNum}>{fmtNum(r.added_tax)}</td>
                  <td style={{ ...ui.tdNum, fontWeight: 700 }}>{fmtNum(r.total_amount)}</td>
                  <td style={ui.td}>{r.evaluation_title}</td>
                  <td style={ui.td}>{r.letter_no}</td>
                  <td style={ui.td}>{r.letter_date}</td>
                  <td style={ui.tdNum}>{toFa(r.detail_count)}</td>
                  <td style={ui.td}>{r.pay_kind_name}</td>
                  <td style={ui.td}>{r.account}</td>
                  <td style={ui.td}>{r.return_date}</td>
                  <td style={ui.td}>{r.begin_date}</td>
                  <td style={ui.td}>{r.finish_date}</td>
                  <td style={ui.td}>{r.budget_date}</td>
                  <td style={ui.td}>{r.havaleh}</td>
                  <td style={ui.td}>{r.sanad}</td>
                  <td style={{ ...ui.tdNum, color: r.reject_count ? '#c62828' : undefined, fontWeight: r.reject_count ? 700 : 400 }}>{toFa(r.reject_count)}</td>
                  <td style={{ ...ui.tdNum, color: r.ok_count ? '#2e7d32' : undefined, fontWeight: r.ok_count ? 700 : 400 }}>{toFa(r.ok_count)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* گرید ۲ — اقلام سند هزینه */}
      <div style={ui.bottomPane}>
      {!selected && (
        <div style={{ ...ui.empty, padding: '18px' }}>
          برای دیدن اقلام سند و اقلام رسیدگی، یک سند هزینه را از جدول بالا انتخاب کنید
        </div>
      )}
      {selected && (
        <div style={{ padding: '4px' }}>
          <div style={{ ...gTitle, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>اقلام سند هزینه — {details.length < dPager.total
              ? `${toFa(details.length)} از ${toFa(dPager.total)} قلم`
              : `${toFa(details.length)} قلم`}</span>
            <ExcelButton title="ریز اقلام سند هزینه در حال رسیدگی"
              columns={xlDetail(levels)} rows={details}
              subtitle={`سند ${toFa(selected.document_no)} — ${selected.document_date}`} />
          </div>
          <div style={{ ...ui.tableWrap, maxHeight: '300px' }}>
            <table style={{ ...ui.table, minWidth: '2400px' }}>
              <thead><tr>
                <th style={ui.th}>شرح</th><th style={ui.th}>فروشنده</th>
                <th style={ui.th}>حساب</th><th style={ui.th}>خطای حساب شبا</th><th style={ui.th}>بانک</th>
                <th style={ui.th}>فاکتور</th><th style={ui.th}>تاریخ</th>
                <th style={ui.th}>تعداد</th><th style={ui.th}>مبلغ واحد</th>
                <th style={ui.th}>مبلغ</th><th style={ui.th}>مالیات بر ارزش افزوده</th>
                <th style={ui.th}>تخفیف</th><th style={ui.th}>قابل پرداخت</th>
                <th style={ui.th}>منبع اعتبار</th><th style={ui.th}>برنامه</th><th style={ui.th}>پروژه</th>
                {(levels.length ? levels : ['فصل','ماده','ریزماده','سطح سوم','سطح چهارم','سطح پنجم'])
                  .map((L, i) => <th key={i} style={ui.th}>{L}</th>)}
                <th style={ui.th}>شماره چک</th><th style={ui.th}>تاریخ چک</th><th style={ui.th}>مبلغ چک</th>
                <th style={ui.th}>شماره رهگیری مالیاتی</th><th style={ui.th}>تاریخ رهگیری</th>
                <th style={ui.th}>شناسه پرداخت</th><th style={ui.th}>شناسه قبض</th>
              </tr></thead>
              <tbody>
                {details.map(r => {
                  const sel = selDetail?.id === r.id;
                  return (
                    <tr key={r.id} onClick={() => pickDetail(r)}
                      style={{ cursor: 'pointer', background: sel ? '#fff8e1' : undefined,
                        outline: sel ? '2px solid #f9a825' : 'none' }}>
                      <td style={ui.td}>{r.remark}</td>
                      <td style={ui.td}>{r.receiver}</td>
                      <td style={ui.td}>{r.account_no}</td>
                      <td style={{ ...ui.td, color: '#c62828', fontWeight: 700 }}>{r.sheba_error}</td>
                      <td style={ui.td}>{r.bank}</td>
                      <td style={ui.td}>{r.factor_no}</td>
                      <td style={ui.td}>{r.factor_date}</td>
                      <td style={ui.tdNum}>{toFa(r.quantity)}</td>
                      <td style={ui.tdNum}>{fmtNum(r.unit_price)}</td>
                      <td style={ui.tdNum}>{fmtNum(r.sum_pay)}</td>
                      <td style={ui.tdNum}>{fmtNum(r.added_tax)}</td>
                      <td style={ui.tdNum}>{fmtNum(r.discount)}</td>
                      <td style={{ ...ui.tdNum, fontWeight: 700 }}>{fmtNum(r.payable)}</td>
                      <td style={ui.td}>{r.credit}</td>
                      <td style={ui.td}>{r.program}</td>
                      <td style={ui.td}>{r.project}</td>
                      <td style={ui.td}>{r.hazineh0}</td><td style={ui.td}>{r.hazineh1}</td>
                      <td style={ui.td}>{r.hazineh2}</td><td style={ui.td}>{r.hazineh3}</td>
                      <td style={ui.td}>{r.hazineh4}</td><td style={ui.td}>{r.hazineh5}</td>
                      <td style={ui.td}>{r.cheque_no}</td>
                      <td style={ui.td}>{r.cheque_date}</td>
                      <td style={ui.tdNum}>{fmtNum(r.cheque_amount)}</td>
                      <td style={ui.td}>{r.tax_ref_no}</td>
                      <td style={ui.td}>{r.tax_ref_date}</td>
                      <td style={ui.td}>{r.slip_id}</td>
                      <td style={ui.td}>{r.bill_id}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <MoreButton pager={dPager} loading={loading} onMore={moreDetails} />

          {/* گریدهای ۳ و ۴ — اقلام رسیدگی */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
            {itemsGrid('اقلام رسیدگی سند', docItems, false)}
            {itemsGrid(selDetail ? 'اقلام رسیدگی قلم انتخابی' : 'اقلام رسیدگی قلم (قلمی انتخاب کنید)', detItems, true)}
          </div>
        </div>
      )}
      </div>
      </div>

      {/* ── مودال تایید / واخواهی ── */}
      {(mt === 'approve' || mt === 'reject' || mt === 'approve_all' || mt === 'approve_all_all') && (
        <div style={ui.overlay}>
          <div style={{ ...ui.modal, maxWidth: '460px' }}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>
                {mt === 'reject' ? 'واخواهی' :
                 mt === 'approve' ? 'تایید' :
                 mt === 'approve_all' ? 'تایید همه موارد' : 'تایید همه موارد همه اقلام سندهزینه'}
              </div>
              <button style={ui.closeBtn} onClick={() => { setModal(null); setReason(''); }}>✕</button>
            </div>
            <div style={ui.mBody}>
              <div style={ui.mlabel}>
                {mt === 'reject' ? 'توضیح واخواهی (الزامی)' : 'توضیح (اختیاری)'}
              </div>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={4}
                style={{ ...ui.input, width: '100%', resize: 'vertical' }} />
              {mt === 'reject' && !reason.trim() && (
                <div style={{ color: '#c62828', fontSize: '12px', marginTop: '6px' }}>
                  واخواهی بدون توضیح ثبت نمی‌شود
                </div>
              )}
            </div>
            <div style={ui.mFooter}>
              <button style={ui.btnCancel} onClick={() => { setModal(null); setReason(''); }}>انصراف</button>
              <button style={ui.btnSave} disabled={busy || (mt === 'reject' && !reason.trim())}
                onClick={() => verdict(
                  mt === 'approve_all_all' ? 'approve_all_all' : mt,
                  { documentId: m.id, fromDetail: m.fromDetail })}>ثبت</button>
            </div>
          </div>
        </div>
      )}

      {/* ── مودال سوابق یک قلم ── */}
      {mt === 'history' && (
        <div style={ui.overlay}>
          <div style={{ ...ui.modal, maxWidth: '640px' }}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>سوابق قلم رسیدگی</div>
              <button style={ui.closeBtn} onClick={() => setModal(null)}>✕</button>
            </div>
            <div style={{ ...ui.mBody, maxHeight: '60vh', overflow: 'auto' }}>
              <table style={ui.table}>
                <thead><tr>
                  <th style={ui.th}>تاریخ</th><th style={ui.th}>زمان</th>
                  <th style={ui.th}>وضعیت</th><th style={ui.th}>توضیح رسیدگی</th>
                </tr></thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr><td colSpan={4} style={ui.empty}>سابقه‌ای نیست</td></tr>
                  ) : history.map((h, i) => (
                    <tr key={i}>
                      <td style={ui.td}>{h.event_date}</td>
                      <td style={ui.td}>{h.event_time}</td>
                      <td style={{ ...ui.td, fontWeight: 700,
                        color: h.status === 'تایید' ? '#2e7d32' : '#c62828' }}>{h.status}</td>
                      <td style={ui.td}>{h.reject_reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── مودال انتخاب گزارش ── */}
      {mt === 'reports' && (
        <div style={ui.overlay}>
          <div style={{ ...ui.modal, maxWidth: '520px' }}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>گزارش‌ها</div>
              <button style={ui.closeBtn} onClick={() => setModal(null)}>✕</button>
            </div>
            <div style={ui.mBody}>
              <div style={ui.row}>
                <div style={{ flex: 1 }}>
                  <div style={ui.flabel}>از تاریخ</div>
                  <JalaliDatePicker value={rFrom} onChange={setRFrom} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={ui.flabel}>تا تاریخ</div>
                  <JalaliDatePicker value={rTo} onChange={setRTo} />
                </div>
              </div>
              <div style={ui.divider} />
              <div style={ui.flabel}>تاریخ ملاک (برای فهرست اسناد رسیدگی)</div>
              <select value={rField} onChange={e => setRField(e.target.value)}
                style={{ ...ui.input, width: '100%' }}>
                {REPORT_DATE_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
              <div style={ui.divider} />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button style={ui.btnNew} disabled={busy} onClick={() => openReport('list')}>📋 فهرست اسناد رسیدگی</button>
                <button style={ui.btnEdit} disabled={busy} onClick={() => openReport('returned')}>↩ اسناد برگشتی</button>
                <button style={ui.btnItems} disabled={busy} onClick={() => openReport('deleted')}>🗑 برگشتی حذف‌شده</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── مودال نمایش گزارش ── */}
      {mt === 'report' && report && (
        <div style={ui.overlay}>
          <div style={{ ...ui.modal, maxWidth: '96vw', width: '1100px' }}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>
                {report.title}
                {report.header && ` — سند ${toFa(report.header.document_no)} (${report.header.document_date})`}
              </div>
              <div>
                <button style={{ ...ui.btnEdit, marginLeft: '8px' }}
                  onClick={() => exportGrid({
                    title: report.title || 'وضعیت اسناد هزینه',
                    columns: (REPORT_COLUMNS[report.kind] || REPORT_COLUMNS.list)
                      .map(c => ({ key: c[0], label: c[1], num: !!c[2] })),
                    rows: report.data || [],
                  })}>⬇ خروجی اکسل</button>
                <button style={ui.btnEdit} onClick={() => window.print()}>🖨 چاپ</button>
                <button style={ui.closeBtn} onClick={() => { setModal(null); setReport(null); }}>✕</button>
              </div>
            </div>
            <div style={{ ...ui.mBody, maxHeight: '70vh', overflow: 'auto' }}>
              {report.header && report.header.reject_count > 0 && (
                <div style={{ color: '#c62828', fontWeight: 700, marginBottom: '8px' }}>
                  تعداد واخواهی: {toFa(report.header.reject_count)}
                </div>
              )}
              <ReportTable report={report} />
            </div>
          </div>
        </div>
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </ListPage>
  );
}

/* ── جدول گزارش — ستون‌ها بر اساس نوع گزارش ── */
const REPORT_COLUMNS = {
  list: [
    ['program_kind_name', 'نوع سند'], ['document_no', 'شماره سند هزینه'],
    ['document_date', 'تاریخ سند هزینه'], ['letter_no', 'شماره دبیرخانه'],
    ['letter_date', 'تاریخ دبیرخانه'], ['remark', 'شرح سند'],
    ['detail_remarks', 'شرح اقلام سند'], ['sum_pay', 'مبلغ سند', 1],
    ['added_tax', 'مالیات بر ارزش افزوده', 1], ['added_insure', 'بیمه سهم دولت', 1],
    ['total', 'جمع', 1],
    ['begin_date', 'شروع رسیدگی'], ['return_date', 'برگشت سند به تدارکات'],
    ['finish_date', 'خاتمه رسیدگی'], ['budget_date', 'ارسال به بودجه'],
  ],
  returned: [
    ['document_no', 'شماره سند هزینه'], ['document_date', 'تاریخ'],
    ['remark', 'شرح'], ['letter_no', 'شماره دبیرخانه'], ['letter_date', 'تاریخ دبیرخانه'],
    ['sum_pay', 'مبلغ', 1], ['return_date', 'تاریخ برگشت'], ['reject_items', 'موارد واخواهی'],
  ],
  deleted: [
    ['document_no', 'سند هزینه'], ['document_date', 'تاریخ'],
    ['remark', 'شرح'], ['amount', 'مبلغ', 1], ['reject_reason', 'توضیحات'],
  ],
  history: [
    ['detail_remark', 'شرح قلم سند'], ['title', 'عنوان رسیدگی'],
    ['event_date', 'تاریخ'], ['event_time', 'زمان'],
    ['result', 'نتیجه'], ['reject_reason', 'توضیح رسیدگی'],
  ],
  result: [
    ['detail_remark', 'شرح قلم سند'], ['receiver', 'فروشنده'], ['sum_pay', 'مبلغ', 1],
    ['title', 'عنوان رسیدگی'], ['event_date', 'تاریخ'], ['event_time', 'زمان'],
    ['result', 'نتیجه'], ['reject_reason', 'توضیح رسیدگی'],
  ],
};

function ReportTable({ report }) {
  const cols = REPORT_COLUMNS[report.kind] || REPORT_COLUMNS.list;
  const rows = report.data || [];
  if (rows.length === 0) return <div style={ui.empty}>📭 رکوردی نیست</div>;
  return (
    <table style={{ ...ui.table, minWidth: '900px' }}>
      <thead><tr>{cols.map(c => <th key={c[0]} style={ui.th}>{c[1]}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {cols.map(c => (
              <td key={c[0]} style={c[2] ? ui.tdNum : ui.td}>
                {c[2] ? fmtNum(r[c[0]]) : String(r[c[0]] ?? '').split('\n').map((ln, k) => (
                  <div key={k}>{ln}</div>
                ))}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── استایل‌های محلی ── */
const lbl = { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12.5px',
  color: '#fff', whiteSpace: 'nowrap', cursor: 'pointer' };

const actBar = { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
  padding: '8px 10px', background: '#f5f7fa', borderBottom: '1px solid #e0e0e0' };

const actTitle = { fontSize: '13px', fontWeight: 800, color: '#1a2f50', marginLeft: '6px' };

const aBtn = (bg) => ({ padding: '6px 12px', background: bg, color: '#fff', border: 'none',
  borderRadius: '7px', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 700,
  cursor: 'pointer', whiteSpace: 'nowrap' });

const miniBtn = (bg) => ({ padding: '3px 8px', background: bg, color: '#fff', border: 'none',
  borderRadius: '5px', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 700,
  cursor: 'pointer', marginLeft: '4px' });

const gTitle = { fontSize: '12.5px', fontWeight: 800, color: '#1a2f50', margin: '6px 2px' };

